<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRouter } from 'vue-router'
import CookieConsentBanner from '@/components/shared/CookieConsentBanner.vue'
import StemneAgreementGateModal from '@/components/ui/StemneAgreementGateModal.vue'
import StemneLoadError from '@/components/ui/StemneLoadError.vue'
import StemnePasskeySudoModal from '@/components/ui/StemnePasskeySudoModal.vue'
import StemneToastContainer from '@/components/ui/StemneToastContainer.vue'
import { useCurrentAdmin } from '@/composables/useCurrentAdmin'
import { useFeatures } from '@/composables/useFeatures'
import { useGoogleAdsTag } from '@/composables/useGoogleAdsTag'
import { useMetaPixel } from '@/composables/useMetaPixel'
import { useOpenAIAdsPixel } from '@/composables/useOpenAIAdsPixel'
import { useTenant } from '@/composables/useTenant'
import type { AgreementKind } from '@/utils/agreementGatePrompt'
import { clearAgreementGatePrompter, setAgreementGatePrompter } from '@/utils/agreementGatePrompt'
import { chunkLoadFailed, retryAfterChunkFailure } from '@/utils/chunkRecovery'
import { clearPasskeySudoPrompter, setPasskeySudoPrompter } from '@/utils/passkeySudoPrompt'

const router = useRouter()
const { t } = useI18n()
const { loadTenant, notFound: tenantNotFound } = useTenant()
const { load: loadFeatures, refresh: refreshFeatures } = useFeatures()
const { fetchCurrentAdmin } = useCurrentAdmin()

// Google Ads conversion tag. Called exactly once, here, because the tag is a
// document-level singleton — a per-view call would race itself. Self-gating
// on marketing host + granted consent, so this is a no-op on every tenant
// subdomain and until the visitor accepts the banner below.
useGoogleAdsTag()

// Meta pixel. Same singleton reasoning and the same two self-gates as the
// Google tag above, so this is likewise a no-op on every tenant subdomain
// and until the visitor accepts. Kept adjacent on purpose: a change to the
// consent policy has to cover every advertising provider.
useMetaPixel()
useOpenAIAdsPixel()

// Global passkey sudo re-auth modal. apiClient.ts calls the registered
// prompter when a passkey mutation returns 403 passkey_sudo_required;
// we open the modal here, return a promise that resolves to the user's
// choice, and let the apiClient retry on true. No watchers — the modal's
// done event resolves the in-flight promise directly.
const sudoModalOpen = ref(false)
let sudoResolver: ((ok: boolean) => void) | null = null

function openSudoPrompt(): Promise<boolean> {
  // Single-flight is handled in passkeySudoPrompt.ts; by the time we get
  // here, only one caller is awaiting. But be defensive: if a stray
  // previous resolver is still around (e.g. hot-reload), resolve it
  // false so it doesn't dangle.
  if (sudoResolver) sudoResolver(false)
  sudoModalOpen.value = true
  return new Promise<boolean>((resolve) => {
    sudoResolver = resolve
  })
}

function onSudoDone(ok: boolean) {
  sudoModalOpen.value = false
  const r = sudoResolver
  sudoResolver = null
  if (r) r(ok)
}

// Global agreement-gate confirmation modal (#3287). Same registry shape as the
// sudo prompter above: apiClient.ts asks before bouncing an operator off to the
// databehandleravtale / operatøravtale accept screen, we open the modal here and
// resolve the in-flight promise from its done event. Declining leaves the
// operator exactly where they were.
const agreementModalOpen = ref(false)
const agreementKind = ref<AgreementKind>('dpa')
let agreementResolver: ((ok: boolean) => void) | null = null

function openAgreementPrompt(kind: AgreementKind): Promise<boolean> {
  // Single-flight lives in agreementGatePrompt.ts, so only one caller per kind
  // reaches us. Be defensive anyway: a dangling resolver (hot reload, or both
  // agreements owed at once) is resolved false rather than left hanging.
  if (agreementResolver) agreementResolver(false)
  agreementKind.value = kind
  agreementModalOpen.value = true
  return new Promise<boolean>((resolve) => {
    agreementResolver = resolve
  })
}

function onAgreementDone(ok: boolean) {
  agreementModalOpen.value = false
  const r = agreementResolver
  agreementResolver = null
  if (r) r(ok)
}

// A lazy route chunk that still failed after the router's evict-and-reload
// recovery (#3874). Without this the failed navigation leaves the router view
// empty — a blank cream page. `retryingChunkLoad` is never reset: the retry ends in a
// full page reload, so the button stays disabled until the page is gone.
const retryingChunkLoad = ref(false)
async function onChunkLoadRetry() {
  retryingChunkLoad.value = true
  await retryAfterChunkFailure()
}

// Refresh feature flags when the tab regains focus — same pattern
// GitHub / Linear use. After a staff member flips a flag and switches
// back to the operator tab, the gated UI updates without a hard
// reload. The backend resolver caches per (org, flag) for 60s so
// this is cheap; the call returns under 20ms when the cache is warm.
//
// Guard against rapid-fire visibility events (devtools docking,
// some Mac windowing edge cases): only refetch when the document
// has been hidden for at least 1s. That cuts spurious refetches
// without losing the "tab back after flipping a flag" use case,
// which always involves more than a second away.
let lastHiddenAt = 0
function onVisibility() {
  if (document.visibilityState === 'hidden') {
    lastHiddenAt = Date.now()
    return
  }
  if (Date.now() - lastHiddenAt < 1000) return
  refreshFeatures()
  // Tab-focus revalidate for /api/admin/me, paired with the 5-min
  // staleness gate in the router. Together they bound /me cadence to
  // "once per visit + once per 5 min of active use" instead of "once
  // per navigation," catching out-of-band session/role changes when
  // the operator switches back to the tab.
  //
  // Operator surfaces only: on public booking/portal pages an
  // anonymous visitor has no admin session, so the probe just 401s
  // (and drags apiClient's refresh retry with it) in every customer's
  // console on every tab refocus.
  const path = router.currentRoute.value.path
  if (path.startsWith('/dashboard') || path.startsWith('/setup') || path.startsWith('/staff')) {
    void fetchCurrentAdmin()
  }
}

onMounted(async () => {
  // #1249 — await loadTenant so we can read notFound after it resolves.
  // If /api/public/tenant 404'd (the tenant doesn't exist on this
  // subdomain), bounce to the branded 404 surface rather than letting
  // the booking layout render with empty heading + "Ingen tjenester er
  // tilgjengelige" — which looks like a working operator with no
  // services rather than a missing tenant. router.replace (not push)
  // so the broken URL isn't in the back-stack.
  await loadTenant()
  if (tenantNotFound.value) {
    router.replace({ name: 'error-not-found' })
  }
  loadFeatures()
  document.addEventListener('visibilitychange', onVisibility)
  setPasskeySudoPrompter(openSudoPrompt)
  setAgreementGatePrompter(openAgreementPrompt)
})
onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibility)
  clearPasskeySudoPrompter()
  clearAgreementGatePrompter()
})
</script>

<template>
  <!-- The data-vaul-drawer-wrapper attribute opts this subtree into
       vaul-vue's `shouldScaleBackground` effect — when a mobile
       drawer opens, this element gets scaled and corner-rounded so
       the page visibly slides back to make room. Without the
       attribute, drawers still work but the page sits flat.
       Toasts live outside the wrapper so they're not scaled with
       the background. -->
  <div data-vaul-drawer-wrapper class="app-wrapper">
    <div v-if="chunkLoadFailed" class="app-chunk-error">
      <StemneLoadError
        :title="t('loadError.pageTitle')"
        :body="t('loadError.pageBody')"
        :retrying="retryingChunkLoad"
        @retry="onChunkLoadRetry"
      />
    </div>
    <RouterView v-else />
  </div>
  <StemnePasskeySudoModal :show="sudoModalOpen" @done="onSudoDone" />
  <StemneAgreementGateModal
    :show="agreementModalOpen"
    :kind="agreementKind"
    @done="onAgreementDone"
  />
  <StemneToastContainer />
  <!-- Marketing-host only, and only while consent is undecided — the
       component gates itself via useCookieConsent, same as the chrome in
       StemneFooter gates on isMarketingHost(). Outside the vaul wrapper so
       an open mobile drawer doesn't scale the banner with the page. -->
  <CookieConsentBanner />
</template>

<style>
/* No padding/margins — the wrapper must be a transparent passthrough
   so the rest of the layout still controls its own dimensions. The
   background-color is what vaul-vue swaps to black during scale (so
   any negative space behind the rounded page reads as the device
   bezel rather than blank cream). */
.app-wrapper {
  background-color: var(--bg-page, #f7f2e8);
  min-height: 100vh;
  /* The one exception to the passthrough rule above: CookieConsentBanner is
     position:fixed and would otherwise float over whatever the page puts at
     the bottom of the viewport. It publishes its measured footprint into
     --cookie-consent-inset while it is on screen, and this reserves it, so
     the page can always scroll its own content clear of the card. 0px the
     rest of the time — which is every visitor who has already decided. */
  padding-bottom: var(--cookie-consent-inset, 0px);
}

/* Failed-chunk panel (#3874): the whole page, so centre it in the viewport
   and cap its width the way a standalone card reads on wide screens. */
.app-chunk-error {
  box-sizing: border-box;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6) var(--space-5);
}

.app-chunk-error > * {
  width: 100%;
  max-width: 480px;
}
</style>
